'use client'

import { useQueries } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'

import { PagesGridIcon } from '@/components/shell-icons'
import { Skeleton } from '@/components/ui/skeleton'
import { useStatusBarShortcuts } from '@/components/widget/status-bar'
import { useAuth } from '@/features/auth/hooks'
import { useNavigateToTradePage } from '@/features/dashboard/hooks/use-navigate-to-trade-page'
import { COMPONENT_REGISTRY, type ComponentKey } from '@/features/dashboard/lib/component-registry'
import { HOME_LIST, HOME_ROW, HOME_SPRING, HomePanel } from '@/features/home/components/home-motion'
import { useRelativeTime } from '@/features/home/hooks/use-relative-time'
import { cn } from '@/lib/utils'
import { getPageLayout } from '@/service/ui-state.api'
import { uiStateKeys } from '@/service/ui-state.hook'
import { activePageIdAtom } from '@/state/dashboard-layouts'
import { pageLastUsedAtAtom } from '@/state/storage'

const MAX_ICONS = 6
/** Number keys 1–9 switch pages anywhere in the app (use-global-keyboard-shortcuts.ts). */
const SHORTCUT_PAGES = 9

/**
 * Every dashboard page as a card — name, what's on it (widget icons), when it was last used and
 * its number-key shortcut — so the tabs that hide each other in the page bar are all visible at
 * once. A card opens its page.
 */
export function HomePages({ className }: { className?: string }) {
  const t = useTranslations('trade.home.pages')
  const tTrade = useTranslations('trade')
  const { user } = useAuth()
  const { orderedPages } = useStatusBarShortcuts()
  const navigateToTradePage = useNavigateToTradePage()
  const activePageId = useAtomValue(activePageIdAtom)
  const lastUsedAt = useAtomValue(pageLastUsedAtAtom)
  const relative = useRelativeTime()

  // Same query key/fn/options as useGetPageLayout, so these read the layouts the dashboard
  // already preloaded instead of fetching again.
  const layouts = useQueries({
    queries: orderedPages.map((page) => ({
      queryKey: uiStateKeys.pageLayout(user?.userId, page.pageId),
      queryFn: () => getPageLayout(page.pageId),
      staleTime: Infinity,
      gcTime: Infinity,
    })),
  })

  return (
    <HomePanel
      title={t('title')}
      icon={PagesGridIcon}
      className={className}
      aside={
        <span className="text-foreground/40 hidden text-xs md:inline">{t('shortcutHint')}</span>
      }
    >
      {orderedPages.length === 0 ? (
        <p className="text-foreground/50 py-4 text-sm">{t('empty')}</p>
      ) : (
        <motion.ul
          variants={HOME_LIST}
          initial="hidden"
          animate="show"
          className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-2"
        >
          {orderedPages.map((page, index) => {
            const layout = layouts[index]
            const widgets = layout?.data?.widgets ?? []
            const keys = [...new Set(widgets.map((widget) => widget.widgetType as ComponentKey))]
            const isActive = page.pageId === activePageId
            const usedAt = lastUsedAt[page.pageId]
            return (
              <motion.li key={page.pageId} variants={HOME_ROW} className="flex">
                <motion.button
                  type="button"
                  onClick={() => navigateToTradePage(page.pageId)}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.98 }}
                  transition={HOME_SPRING}
                  className={cn(
                    'group bg-foreground/[0.03] hover:bg-foreground/[0.06] relative flex h-full w-full cursor-pointer flex-col gap-3 rounded-md border p-3 text-left transition-colors',
                    isActive
                      ? 'border-shell-accent/50'
                      : 'border-foreground/10 hover:border-foreground/20'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium tracking-tight">
                      {page.name}
                    </span>
                    {isActive && (
                      <span className="bg-shell-accent/15 text-shell-accent rounded-full px-1.5 py-0.5 text-[10px] font-medium">
                        {t('current')}
                      </span>
                    )}
                    {index < SHORTCUT_PAGES && (
                      <kbd className="border-foreground/15 text-foreground/45 rounded-xs border px-1 font-sans text-[10px]">
                        {index + 1}
                      </kbd>
                    )}
                  </span>
                  <span className="flex min-h-6 items-center gap-1">
                    {layout?.isPending ? (
                      <Skeleton className="h-6 w-28" />
                    ) : keys.length === 0 ? (
                      <span className="text-foreground/40 text-xs">{t('noWidgets')}</span>
                    ) : (
                      <>
                        {keys.slice(0, MAX_ICONS).map((key, iconIndex) => {
                          const entry = COMPONENT_REGISTRY[key]
                          if (!entry) return null
                          const Icon = entry.icon
                          const label = tTrade.has(`widgets.${key}`)
                            ? tTrade(`widgets.${key}`)
                            : entry.label
                          return (
                            <span
                              key={key}
                              title={label}
                              className="bg-foreground/5 text-foreground/60 group-hover:text-foreground/80 flex size-6 items-center justify-center rounded-sm transition-[color,translate] duration-300 group-hover:-translate-y-0.5"
                              style={{ transitionDelay: `${iconIndex * 25}ms` }}
                            >
                              <Icon className="size-3.5" />
                            </span>
                          )
                        })}
                        {keys.length > MAX_ICONS && (
                          <span className="text-foreground/45 pl-1 text-xs">
                            +{keys.length - MAX_ICONS}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <span className="text-foreground/40 flex items-center justify-between text-xs">
                    <span>{t('widgets', { count: widgets.length })}</span>
                    {usedAt ? <span>{relative(usedAt)}</span> : null}
                  </span>
                </motion.button>
              </motion.li>
            )
          })}
        </motion.ul>
      )}
    </HomePanel>
  )
}
